import { AutomationValue } from '../../../automation/ast/AutomationValue.js';
import { parse as parseYaml } from 'yaml';
import {
  selectorDefinition,
  selectorForServiceField,
} from '../metadata/selectorModel.js';

/** Return the service fields in the shape used by Home Assistant's /api/services response. */
export function serviceFields(serviceOption) {
  const fields = serviceOption?.fields;
  return fields && typeof fields === 'object' && !Array.isArray(fields) ? fields : {};
}

export function serviceFieldEntries(serviceOption) {
  return Object.entries(serviceFields(serviceOption)).filter(([key]) => key !== 'entity_id').map(([key, definition]) => ({
    key,
    definition: definition && typeof definition === 'object' && !Array.isArray(definition)
      ? definition
      : {},
  }));
}

export function fieldSelector(definition = {}) {
  return selectorDefinition(definition);
}

export { selectorForServiceField };

export function fieldOptions(definition) {
  const { config } = fieldSelector(definition);
  const options = config?.options ?? [];
  return Array.isArray(options)
    ? options.map(option => typeof option === 'object' && option !== null
      ? { value: option.value ?? option.label ?? '', label: option.label ?? option.value ?? '' }
      : { value: option, label: option })
    : [];
}

function isTemplate(value) {
  return typeof value === 'string' && /{{[\s\S]*}}/.test(value);
}

function parseRgbValue(value) {
  if (Array.isArray(value)) return value;
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || /{{[\s\S]*}}/.test(text)) return null;
  const hex = /^#?([\da-f]{6})$/i.exec(text);
  if (hex) {
    return [1, 3, 5].map(offset => Number.parseInt(hex[1].slice(offset - 1, offset + 1), 16));
  }
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // Fall through to the compact comma-separated form used by the editor.
  }
  const parts = text.split(',').map(part => part.trim()).filter(Boolean);
  if (parts.length !== 3 || parts.some(part => !Number.isFinite(Number(part)))) return null;
  return parts.map(Number);
}

export const SERVICE_VALUE_SOURCES = Object.freeze([
  { value: 'object', label: 'Object Property' },
  { value: 'literal', label: 'Literal' },
  { value: 'event', label: 'Event Value' },
]);

export function serviceFieldSource(parameter) {
  if (SERVICE_VALUE_SOURCES.some(source => source.value === parameter?.source)) return parameter.source;
  const value = parameter?.value;
  const text = value instanceof AutomationValue ? value.toString() : String(value ?? '');
  if (text.startsWith('object.')) return 'object';
  if (value instanceof AutomationValue && ['template', 'reference'].includes(value.kind)) return 'event';
  if (typeof value === 'string' && /{{\s*(?:val|text|[a-zA-Z_]\w*)\b/.test(value)) return 'event';
  // A field without a saved parameter starts in the historical object-property
  // mode. Existing scalar parameters remain literal, while the two dynamic
  // legacy forms above retain their original meaning.
  return parameter == null ? 'object' : 'literal';
}

export function serviceFieldSourceValue(value, source, definition = {}) {
  if (source === 'event') {
    if (value instanceof AutomationValue && value.kind === 'reference') {
      return AutomationValue.template(`{{ ${value.name} }}`);
    }
    const text = value instanceof AutomationValue ? value.toString() : String(value ?? '');
    if (!text.trim()) return AutomationValue.template('');
    if (/{{[\s\S]*}}/.test(text)) return AutomationValue.template(text);
    return AutomationValue.template(`{{ ${text.replace(/^{{|}}$/g, '').trim()} }}`);
  }
  if (source === 'object') {
    return AutomationValue.literal(value instanceof AutomationValue ? value.toString() : (value ?? ''));
  }
  if (value instanceof AutomationValue && ['template', 'reference'].includes(value.kind)) {
    return serviceFieldValue(value.toString(), definition);
  }
  return serviceFieldValue(value, definition);
}

export function serviceFieldLiteralKind(definition = {}) {
  const { kind, config } = fieldSelector(definition);
  if (kind === 'number') return 'number';
  if (kind === 'boolean') return 'boolean';
  if (kind === 'color_rgb') return 'color_rgb';
  if (kind === 'array') return 'array';
  if (kind === 'select' && config.multiple === true) return 'array';
  if (kind === 'select') return 'enum';
  if (kind === 'object') return 'object';
  return 'string';
}

export function serviceFieldColorHex(value) {
  const rgb = parseRgbValue(value instanceof AutomationValue ? value.toString() : value);
  if (!rgb || rgb.length !== 3 || rgb.some(channel => !Number.isFinite(Number(channel)))) return '#000000';
  return `#${rgb.map(channel => Math.max(0, Math.min(255, Number(channel)))
    .toString(16).padStart(2, '0')).join('')}`;
}

function parseObjectValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value !== 'string' || !value.trim()) return null;
  try {
    const parsed = parseYaml(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Preserve Jinja as a template value and keep selector booleans/numbers typed. */
export function serviceFieldValue(value, definition) {
  if (value instanceof AutomationValue) return value;
  if (value && typeof value === 'object' && typeof value.kind === 'string') {
    return AutomationValue.fromJSON(value);
  }
  if (isTemplate(value)) return AutomationValue.template(value);

  const { kind } = fieldSelector(definition);
  if (kind === 'boolean') {
    const booleanValue = value === true || value === 1 || value === '1' || value === 'true';
    return new AutomationValue({ kind: 'literal', value: booleanValue });
  }
  if (kind === 'number' && value !== '' && value != null && Number.isFinite(Number(value))) {
    return new AutomationValue({ kind: 'literal', value: Number(value) });
  }
  if (kind === 'color_rgb') {
    const rgb = parseRgbValue(value);
    if (rgb) return AutomationValue.list(rgb);
  }
  if (kind === 'object') {
    const objectValue = parseObjectValue(value);
    if (objectValue) return AutomationValue.object(objectValue);
  }
  if (Array.isArray(value)) return AutomationValue.list(value);
  // Keep the value available for future selector kinds without coercing it to
  // a domain-specific object that Home Assistant may not accept.
  return AutomationValue.literal(value ?? '');
}

export function serviceFieldDisplayValue(value) {
  if (value == null) return '';
  if (value instanceof AutomationValue) {
    if (value.kind === 'list') return value.items.join(', ');
    if (value.kind === 'object') return JSON.stringify(value.value);
    return String(value.toString());
  }
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

export function serviceFieldRawValue(action, key, definition) {
  const parameter = action?.parameters?.find(item => item.key === key);
  if (parameter?.value != null) return parameter.value;
  if (action?.data && Object.prototype.hasOwnProperty.call(action.data, key)) return action.data[key];
  if (Object.prototype.hasOwnProperty.call(definition ?? {}, 'default')) return definition.default;
  return '';
}

export function serviceFieldParameter(action, key) {
  return action?.parameters?.find(item => item.key === key) ?? null;
}

/** Resolve both friendly (provider + service) and advanced service identifiers. */
export function serviceOptionForAction(action, options = []) {
  const service = action?.provider && action?.service && !String(action.service).includes('.')
    ? `${action.provider}.${action.service}`
    : action?.service;
  return options.find(option => option.value === service) ?? null;
}

export function serviceFieldIsEmpty(value) {
  if (value instanceof AutomationValue) {
    if (value.kind === 'list') return value.items.length === 0;
    if (value.kind === 'object') return Object.keys(value.value ?? {}).length === 0;
    return String(value.toString()).trim() === '';
  }
  return value == null || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);
}

/** Validate only the active service field; templates are valid dynamic values. */
export function validateServiceFieldValue(value, definition = {}) {
  if (serviceFieldIsEmpty(value)) return { valid: definition.required !== true, message: 'This field is required.' };
  if (value instanceof AutomationValue && ['template', 'reference'].includes(value.kind)) return { valid: true, message: '' };

  const { kind, config } = fieldSelector(definition);
  if (kind === 'number') {
    const number = Number(value instanceof AutomationValue ? value.value : value);
    if (!Number.isFinite(number)) return { valid: false, message: 'Enter a number or a dynamic value.' };
    if (config.min != null && number < Number(config.min)) return { valid: false, message: `Minimum is ${config.min}.` };
    if (config.max != null && number > Number(config.max)) return { valid: false, message: `Maximum is ${config.max}.` };
    if (config.step != null && Number(config.step) > 0 && config.min != null) {
      const distance = (number - Number(config.min)) / Number(config.step);
      if (Math.abs(distance - Math.round(distance)) > 1e-7) return { valid: false, message: `Use increments of ${config.step}.` };
    }
  }
  if (kind === 'select' || kind === 'multi_select') {
    const values = fieldOptions(definition).map(option => String(option.value));
    const selected = value instanceof AutomationValue && value.kind === 'list'
      ? value.items
      : value instanceof AutomationValue
        ? value.toString()
        : value;
    if (config.multiple === true) {
      const items = Array.isArray(selected) ? selected : [selected];
      if (items.some(item => !values.includes(String(item)))) return { valid: false, message: 'Select a value from the available options.' };
    } else if (!values.includes(String(selected))) {
      return { valid: false, message: 'Select a value from the available options.' };
    }
  }
  if (kind === 'color_rgb' && !parseRgbValue(value instanceof AutomationValue ? value.toString() : value)) {
    return { valid: false, message: 'Use three RGB values, for example 255, 0, 0.' };
  }
  if (kind === 'object' && !(value instanceof AutomationValue && value.kind === 'object') && !parseObjectValue(value)) {
    return { valid: false, message: 'Enter a valid YAML or JSON object.' };
  }
  return { valid: true, message: '' };
}

export function requiredServiceFieldKeys(serviceOption) {
  return serviceFieldEntries(serviceOption)
    .filter(({ definition }) => definition.required === true)
    .map(({ key }) => key);
}

export function missingRequiredServiceFields(action, serviceOption) {
  return requiredServiceFieldKeys(serviceOption).filter(key => {
    const definition = serviceFields(serviceOption)[key];
    return serviceFieldIsEmpty(serviceFieldRawValue(action, key, definition));
  });
}

/** Seed fields from saved data or HA defaults without overwriting authored values. */
export function initializeServiceFields(action, serviceOption, { includeDefaults = true } = {}) {
  if (!action) return;
  action.parameters ??= [];
  for (const { key, definition } of serviceFieldEntries(serviceOption)) {
    if (action.parameters.some(parameter => parameter.key === key)) continue;
    let value;
    if (action.data && Object.prototype.hasOwnProperty.call(action.data, key)) value = action.data[key];
    else if (includeDefaults && Object.prototype.hasOwnProperty.call(definition, 'default')) value = definition.default;
    else continue;
    action.parameters.push({ key, value: serviceFieldValue(value, definition) });
  }
}

function serializableValue(value) {
  return value instanceof AutomationValue ? value.toJSON() : value;
}

function serviceCache(action) {
  action.editorMetadata ??= {};
  action.editorMetadata.serviceFieldCache ??= {};
  return action.editorMetadata.serviceFieldCache;
}

/** Move fields known to the previous service out of active parameters. */
export function cacheServiceFields(action, serviceOption) {
  const serviceId = serviceOption?.value;
  if (!action || !serviceId) return;
  const cache = serviceCache(action);
  const values = { ...(cache[serviceId] ?? {}) };
  for (const { key } of serviceFieldEntries(serviceOption)) {
    const parameter = action.parameters?.find(item => item.key === key);
    if (parameter) {
      values[key] = parameter.source == null
        ? serializableValue(parameter.value)
        : { source: parameter.source, value: serializableValue(parameter.value) };
      action.parameters = action.parameters.filter(item => item !== parameter);
    } else if (action.data && Object.prototype.hasOwnProperty.call(action.data, key)) {
      values[key] = serializableValue(action.data[key]);
      const { [key]: ignored, ...rest } = action.data;
      action.data = rest;
    }
  }
  cache[serviceId] = values;
}

/** Restore fields previously edited for this service without overwriting current values. */
export function restoreServiceFields(action, serviceOption) {
  const serviceId = serviceOption?.value;
  if (!action || !serviceId) return;
  const values = serviceCache(action)[serviceId] ?? {};
  action.parameters ??= [];
  for (const { key } of serviceFieldEntries(serviceOption)) {
    if (!Object.prototype.hasOwnProperty.call(values, key)) continue;
    if (!action.parameters.some(parameter => parameter.key === key)) {
      const saved = values[key];
      const wrapped = saved && typeof saved === 'object' &&
        Object.hasOwn(saved, 'value') && !Object.hasOwn(saved, 'kind');
      action.parameters.push({
        key,
        ...(wrapped && saved.source != null ? { source: saved.source } : {}),
        value: serviceFieldValue(wrapped ? saved.value : saved, serviceFields(serviceOption)[key]),
      });
    }
  }
}

export function switchServiceFields(action, previousService, nextService) {
  if (!action) return;
  if (previousService?.value && previousService.value !== nextService?.value) {
    cacheServiceFields(action, previousService);
  }
  restoreServiceFields(action, nextService);
}
